import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { cnpjMask } from '@/components/Deals/DealDetailPanel/utils'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { Proposta } from '@/types'
import {
  buildPublicCadastroPayload,
  isPublicContractReady,
  normalizeProposta,
  type CustomerData,
  type WizardStep,
} from '@/utils/propostaPublicaUtils'

export function usePropostaPublica(proposalId: string | undefined) {
  const [proposta, setProposta] = useState<Proposta | null>(null)
  const [loading, setLoading] = useState(true)
  const [aceitando, setAceitando] = useState(false)
  const [aceita, setAceita] = useState(false)
  const [erro, setErro] = useState('')
  const [step, setStep] = useState<WizardStep>(1)

  const [customer, setCustomer] = useState<CustomerData>({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    cargo: '',
    cnpj: '',
    responsavelFinanceiroNome: '',
    emailFinanceiro: '',
  })

  const [cadastroSave, setCadastroSave] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const editedCadastro = useRef(false)
  const saveSeq = useRef(0)

  const [showRevisao, setShowRevisao] = useState(false)
  const [revisaoForm, setRevisaoForm] = useState({ nome: '', email: '', telefone: '', mensagem: '' })
  const [revisaoLoading, setRevisaoLoading] = useState(false)
  const [revisaoEnviada, setRevisaoEnviada] = useState(false)

  const submitTracking = useCallback(async () => {
    if (!proposalId) return
    try {
      await api.post(`/propostas/${proposalId}/track`, { action: 'visualizou' })
    } catch {
      // tracking is best-effort
    }
  }, [proposalId])

  const fetchProposal = useCallback(async () => {
    if (!proposalId) {
      setErro('ID da proposta não informado.')
      setLoading(false)
      return
    }

    const endpoints = [`/propostas/public/id/${proposalId}`, `/propostas/public/${proposalId}`]

    for (const endpoint of endpoints) {
      try {
        const { data } = await api.get(endpoint)
        const normalized = normalizeProposta(data)
        const cadastro = (data?.cadastro ?? {}) as {
          empresa?: string
          cnpj?: string
          responsavelAssinaturaNome?: string
          responsavelAssinaturaEmail?: string
          responsavelFinanceiroNome?: string
          emailFinanceiro?: string
        }
        setProposta(normalized)
        setCustomer((prev) => ({
          ...prev,
          empresa: cadastro.empresa || prev.empresa,
          cnpj: cadastro.cnpj ? cnpjMask(cadastro.cnpj) : prev.cnpj,
          nome: cadastro.responsavelAssinaturaNome || prev.nome,
          email: cadastro.responsavelAssinaturaEmail || prev.email,
          responsavelFinanceiroNome: cadastro.responsavelFinanceiroNome || prev.responsavelFinanceiroNome,
          emailFinanceiro: cadastro.emailFinanceiro || prev.emailFinanceiro,
        }))
        if (normalized.status === 'aceita') {
          setAceita(true)
          setStep(3)
        }
        return normalized
      } catch {
        // tenta a próxima rota
      }
    }

    throw new Error('Não foi possível carregar esta proposta.')
  }, [proposalId])

  useEffect(() => {
    async function load() {
      try {
        const p = await fetchProposal()
        if (p) submitTracking()
      } catch {
        setErro('Não foi possível carregar esta proposta. Ela pode ter expirado ou sido removida.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [fetchProposal, submitTracking])

  const patchCustomer = useCallback((patch: Partial<CustomerData>) => {
    editedCadastro.current = true
    setCustomer((prev) => ({ ...prev, ...patch }))
  }, [])

  useEffect(() => {
    if (!proposalId || step !== 2 || !editedCadastro.current) return
    const payload = buildPublicCadastroPayload(customer)
    if (!payload) return
    const timer = window.setTimeout(() => {
      const seq = ++saveSeq.current
      setCadastroSave('saving')
      api
        .patch(`/propostas/public/id/${proposalId}/cadastro`, payload)
        .then(() => {
          if (saveSeq.current === seq) setCadastroSave('saved')
        })
        .catch((err: unknown) => {
          if (saveSeq.current !== seq) return
          setCadastroSave('error')
          toast.error(getFriendlyError(err, 'Não foi possível salvar o cadastro'))
        })
    }, 700)
    return () => window.clearTimeout(timer)
  }, [customer, proposalId, step])

  const handleAceitar = async () => {
    if (!proposalId || !proposta || !isPublicContractReady(customer)) return
    setAceitando(true)
    try {
      try {
        await api.patch(`/propostas/${proposalId}/aceitar`, {
          signerName: customer.nome,
          signerEmail: customer.email,
          cnpj: customer.cnpj,
          empresaNome: customer.empresa,
          responsavelFinanceiroNome: customer.responsavelFinanceiroNome,
          emailFinanceiro: customer.emailFinanceiro,
        })
      } catch {
        await api.post(`/propostas/public/${proposalId}/accept`, {
          signerName: customer.nome,
          signerEmail: customer.email,
          cnpj: customer.cnpj,
          empresaNome: customer.empresa,
          responsavelFinanceiroNome: customer.responsavelFinanceiroNome,
          emailFinanceiro: customer.emailFinanceiro,
          acceptedTerms: true,
        })
      }
      setAceita(true)
      setProposta((prev) => (prev ? { ...prev, status: 'aceita' } : prev))
      setStep(3)
      toast.success('Proposta aceita com sucesso!')
    } catch {
      toast.error('Erro ao aceitar proposta. Tente novamente.')
    } finally {
      setAceitando(false)
    }
  }

  const handleSolicitarRevisao = async () => {
    if (!proposalId) return
    setRevisaoLoading(true)
    try {
      await api.post(`/propostas/${proposalId}/solicitar-revisao`, revisaoForm)
      setRevisaoEnviada(true)
      toast.success('Solicitação de revisão enviada com sucesso!')
    } catch {
      toast.error('Erro ao enviar solicitação. Tente novamente.')
    } finally {
      setRevisaoLoading(false)
    }
  }

  const canGoNext = step === 1 || (step === 2 && isPublicContractReady(customer))

  return {
    proposta,
    loading,
    aceitando,
    aceita,
    erro,
    step,
    setStep,
    customer,
    setCustomer,
    patchCustomer,
    cadastroSave,
    showRevisao,
    setShowRevisao,
    revisaoForm,
    setRevisaoForm,
    revisaoLoading,
    revisaoEnviada,
    setRevisaoEnviada,
    handleAceitar,
    handleSolicitarRevisao,
    canGoNext,
  }
}
